import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  MultiSelect,
  MultiSelectValueProps,
  ScrollArea,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import useAutoCompleteProduct from 'hooks/product/useAutoCompleteProduct';
import { Product } from 'types';

const PILL_GAP = 8;
const OVERFLOW_BADGE_RESERVE = 26;
const INPUT_CHROME_RESERVE = 26;

interface IProductSelectModalFieldProps {
  label: string;
  type: Product['type'];
  value: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  modalDescription?: string;
}

const ProductSelectModalField = ({
  label,
  type,
  value,
  onChange,
  disabled = false,
  modalDescription,
}: IProductSelectModalFieldProps): JSX.Element => {
  const theme = useMantineTheme();
  const [opened, setOpened] = useState(false);
  const [staged, setStaged] = useState<Set<string>>(new Set(value));

  const { data, isLoading } = useAutoCompleteProduct({ type });
  const products = useMemo(() => data?.data ?? [], [data]);

  const multiSelectData = useMemo(
    () => products.map(p => ({ value: p.productId, label: p.name })),
    [products],
  );

  const labelByValue = useMemo(
    () => Object.fromEntries(multiSelectData.map(d => [d.value, d.label])),
    [multiSelectData],
  );

  const wrapperRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(value.length);

  useLayoutEffect(() => {
    const compute = (): void => {
      const wrapper = wrapperRef.current;
      const measure = measureRef.current;
      if (!wrapper || !measure) return;
      if (value.length === 0) {
        setVisibleCount(0);
        return;
      }
      const containerW = wrapper.clientWidth - INPUT_CHROME_RESERVE;
      const widths = Array.from(measure.children).map(
        el => (el as HTMLElement).getBoundingClientRect().width,
      );
      let used = 0;
      let n = 0;
      for (let i = 0; i < value.length; i++) {
        const next = used + widths[i] + (i > 0 ? PILL_GAP : 0);
        const needsBadge = i < value.length - 1;
        const required = next + (needsBadge ? PILL_GAP + OVERFLOW_BADGE_RESERVE : 0);
        if (required > containerW) break;
        used = next;
        n++;
      }
      setVisibleCount(n);
    };

    compute();
    const ro = new ResizeObserver(compute);
    if (wrapperRef.current) ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, [value, labelByValue]);

  useEffect(() => {
    if (opened) setStaged(new Set(value));
  }, [opened, value]);

  const allSelected = products.length > 0 && products.every(p => staged.has(p.productId));

  const toggle = (productId: string): void => {
    setStaged(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleAll = (): void => {
    if (allSelected) setStaged(new Set());
    else setStaged(new Set(products.map(p => p.productId)));
  };

  const handleConfirm = (): void => {
    onChange(Array.from(staged));
    setOpened(false);
  };

  const handleCancel = (): void => {
    setOpened(false);
  };

  const renderValue = useCallback(
    ({ value: itemValue, label: itemLabel }: MultiSelectValueProps & { value: string }) => {
      const idx = value.indexOf(itemValue);
      if (idx >= visibleCount) {
        if (idx === visibleCount && value.length > visibleCount) {
          return (
            <Badge
              variant='light'
              color='kiloBlue'
              radius='sm'
              size='lg'
              px='xs'
              sx={{ marginLeft: 'auto', flexShrink: 0 }}
            >
              +{value.length - visibleCount}
            </Badge>
          );
        }
        return null;
      }

      return (
        <Badge
          variant='light'
          color='gray'
          radius='sm'
          size='lg'
          maw={220}
          sx={{ flexShrink: 1, minWidth: 0, marginRight: '8px' }}
          styles={{ inner: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
          rightSection={
            <IconX
              size={12}
              alignmentBaseline='central'
              style={{ cursor: 'pointer' }}
              onMouseDown={e => {
                e.stopPropagation();
                e.preventDefault();
                onChange(value.filter(v => v !== itemValue));
              }}
            />
          }
        >
          {itemLabel}
        </Badge>
      );
    },
    [value, visibleCount, onChange],
  );

  return (
    <>
      <div ref={wrapperRef}>
        <MultiSelect
          label={label}
          data={multiSelectData}
          value={value}
          onChange={onChange}
          placeholder={`Select ${label.toLowerCase()}`}
          searchable={false}
          disabled={disabled}
          onDropdownOpen={() => !disabled && setOpened(true)}
          valueComponent={renderValue}
          styles={{
            dropdown: { display: 'none' },
            values: { flexWrap: 'nowrap', overflow: 'hidden', minWidth: 0 },
          }}
        />
      </div>

      <div
        ref={measureRef}
        aria-hidden
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          left: -99999,
          top: 0,
          display: 'flex',
          gap: PILL_GAP,
        }}
      >
        {value.map(v => (
          <Badge
            key={v}
            variant='light'
            color='gray'
            radius='sm'
            size='lg'
            maw={220}
            styles={{ inner: { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            rightSection={<IconX size={12} />}
          >
            {labelByValue[v] ?? v}
          </Badge>
        ))}
      </div>

      <Modal
        opened={opened}
        onClose={handleCancel}
        title={
          <Title order={4} italic>
            {label}
          </Title>
        }
        centered
        size='md'
      >
        <Stack spacing='sm'>
          <Group position='apart'>
            <Text size='sm' color='gray'>
              {modalDescription ?? `Select one or multiple ${label.toLowerCase()}`}
            </Text>
            <Anchor size='sm' onClick={toggleAll}>
              {allSelected ? 'Unselect All' : 'Select All'}
            </Anchor>
          </Group>
          <ScrollArea h={400}>
            {isLoading ? (
              <Group position='center' py='lg'>
                <Loader size='sm' />
              </Group>
            ) : products.length === 0 ? (
              <Text size='sm' color='gray' align='center' py='lg'>
                No {label.toLowerCase()} found
              </Text>
            ) : (
              <Stack spacing={4}>
                {products.map(p => {
                  const isSelected = staged.has(p.productId);
                  return (
                    <Group
                      key={p.productId}
                      position='apart'
                      onClick={() => toggle(p.productId)}
                      sx={t => ({
                        padding: '10px 12px',
                        borderRadius: t.radius.sm,
                        cursor: 'pointer',
                        background: isSelected ? '#15B8FF0D' : 'transparent',
                      })}
                    >
                      <Text size='sm'>{p.name}</Text>
                      {isSelected && <IconCheck size={16} color={theme.colors.kiloBlue[6]} />}
                    </Group>
                  );
                })}
              </Stack>
            )}
          </ScrollArea>
          <Group position='apart' pt='sm'>
            <Button variant='subtle' onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={staged.size === 0}>
              Confirm Selection
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};

export default ProductSelectModalField;
